import { formatDateDisplay } from "@/lib/timezone";
import { AttendanceCellBadge } from "@/components/attendance/attendance-cell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CATEGORY_LABELS } from "@/lib/types";
import type { MonthlyRow } from "@/lib/types";

interface PersonMonthlyDrilldownProps {
  open: boolean;
  onClose: () => void;
  row: MonthlyRow | null;
  tableLabel: string;
  dates: string[];
}

export function PersonMonthlyDrilldown({
  open,
  onClose,
  row,
  tableLabel,
  dates,
}: PersonMonthlyDrilldownProps) {
  if (!open || !row) return null;

  const s = row.summary;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center">
      <div
        className="flex max-h-[85vh] w-full max-w-lg flex-col rounded-lg border bg-white shadow-lg"
        role="dialog"
        aria-modal="true"
      >
        <div className="border-b px-4 py-3">
          <h3 className="text-lg font-semibold">{row.person_name}</h3>
          <p className="text-sm text-muted-foreground">
            {tableLabel}
            {row.category && (
              <>
                {" "}
                · <Badge variant="secondary">{CATEGORY_LABELS[row.category]}</Badge>
              </>
            )}
          </p>
        </div>

        <div className="grid grid-cols-3 gap-2 border-b px-4 py-3 text-center text-sm sm:grid-cols-6">
          <Stat label="Present" value={s.present} />
          <Stat label="Late" value={s.late} />
          <Stat label="Absent" value={s.absent} />
          <Stat label="On leave" value={s.on_leave} />
          <Stat label="Holiday" value={s.holiday} />
          <Stat
            label="Present %"
            value={s.present_pct != null ? `${s.present_pct}%` : "—"}
            highlight
          />
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          <ul className="space-y-1 text-sm">
            {dates.map((d) => {
              const cell = row.cells[d];
              return (
                <li
                  key={d}
                  className="flex items-center justify-between rounded-md px-2 py-1.5 hover:bg-muted/40"
                >
                  <span className="text-muted-foreground">
                    {formatDateDisplay(d)}
                  </span>
                  <span className="flex items-center gap-2">
                    {cell?.holiday_name && (
                      <span className="text-xs text-violet-700">
                        {cell.holiday_name}
                      </span>
                    )}
                    <AttendanceCellBadge cell={cell ?? { status: "pending" }} />
                  </span>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="border-t px-4 py-3">
          <Button variant="outline" className="w-full" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string | number;
  highlight?: boolean;
}) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p
        className={`font-semibold tabular-nums ${highlight ? "text-emerald-700" : ""}`}
      >
        {value}
      </p>
    </div>
  );
}
